/**
 * API Service
 *
 * Handles all HTTP communication with the backend API.
 * Includes automatic token refresh and error handling.
 */

import type { ApiResponse, TokenPair } from '../types';
import { getSupportToken } from '../hooks/useSupportSession';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Token storage
let accessToken: string | null = null;
let refreshToken: string | null = null;
let refreshPromise: Promise<void> | null = null;

export function setTokens(tokens: TokenPair): void {
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
  // Store refresh token in localStorage for persistence
  localStorage.setItem('refreshToken', tokens.refreshToken);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('refreshToken');
}

export function loadStoredRefreshToken(): void {
  refreshToken = localStorage.getItem('refreshToken');
}

/**
 * Refresh the access token
 */
export async function refreshAccessToken(): Promise<void> {
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Prevent multiple refresh requests
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = (await response.json()) as ApiResponse<TokenPair>;
      if (data.success && data.data) {
        setTokens(data.data);
      } else {
        throw new Error('Token refresh failed');
      }
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Make an API request with automatic token refresh
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Add support token for admin access to user data
  const supportToken = getSupportToken();
  if (supportToken) {
    headers['X-Support-Token'] = supportToken;
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized, try to refresh token
  if (response.status === 401 && refreshToken) {
    try {
      await refreshAccessToken();
      // Retry the request with new token
      headers['Authorization'] = `Bearer ${accessToken}`;
      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch {
      // Refresh failed, clear tokens
      clearTokens();
      throw new Error('Session expired. Please log in again.');
    }
  }

  const data = (await response.json()) as ApiResponse<T>;

  if (!response.ok || !data.success) {
    throw new Error(data.error || `Request failed: ${response.status}`);
  }

  return data;
}

// ============================================
// AUTH API
// ============================================

export const authApi = {
  async register(data: {
    email: string;
    password: string;
    name: string;
    publicKey: string;
    encryptedPrivateKey: string;
  }) {
    return request<{ id: string; email: string; name: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async login(data: { email: string; password: string; twoFactorCode?: string }) {
    return request<{ user?: any; tokens?: TokenPair; requires2FA?: boolean }>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  async logout() {
    try {
      await request('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } finally {
      clearTokens();
    }
  },

  async getMe() {
    try {
      return await request<any>('/auth/me');
    } catch (error) {
      // Expected 401 when not logged in - return error response silently
      return { success: false, error: 'Not authenticated', data: null };
    }
  },

  async verifyEmail(token: string) {
    return request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  async forgotPassword(email: string) {
    return request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(data: {
    token: string;
    password: string;
    publicKey: string;
    encryptedPrivateKey: string;
  }) {
    return request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
    publicKey: string;
    encryptedPrivateKey: string;
    reEncryptedBabyKeys?: Record<string, string>;
  }) {
    return request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async setup2FA() {
    return request<{ secret: string; qrCodeUrl: string }>('/auth/setup-2fa', {
      method: 'POST',
    });
  },

  async verify2FA(code: string) {
    return request('/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async disable2FA(password: string) {
    return request('/auth/disable-2fa', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  async deleteAccount(password: string) {
    return request('/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  },

  async updateProfile(data: { name: string }) {
    return request<{ name: string }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// BABY API
// ============================================

export const babyApi = {
  async create(data: { encryptedData: string; encryptedBabyKey: string; profileType?: string }) {
    return request<any>('/babies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async list() {
    return request<any[]>('/babies');
  },

  async getById(babyId: string) {
    return request<any>(`/babies/${babyId}`);
  },

  async update(babyId: string, data: { encryptedData: string; profileType?: string }) {
    return request<any>(`/babies/${babyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async delete(babyId: string) {
    return request(`/babies/${babyId}`, {
      method: 'DELETE',
    });
  },

  async getMembers(babyId: string) {
    return request<any[]>(`/babies/${babyId}/members`);
  },

  async removeMember(babyId: string, userId: string) {
    return request(`/babies/${babyId}/members/${userId}`, {
      method: 'DELETE',
    });
  },

  async updateProfilePhoto(babyId: string, photoId: string | null) {
    return request<{ id: string; profilePhotoId: string | null }>(
      `/babies/${babyId}/profile-photo`,
      {
        method: 'PATCH',
        body: JSON.stringify({ photoId }),
      }
    );
  },
};

// ============================================
// EVENT API
// ============================================

export const eventApi = {
  async create(
    babyId: string,
    data: {
      type: string;
      timestamp: string;
      encryptedData: string;
      encryptedNotes?: string;
      photoIds?: string[];
    }
  ) {
    return request<any>(`/babies/${babyId}/events`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async list(babyId: string, params?: Record<string, any>) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return request<{ events: any[] }>(`/babies/${babyId}/events${query}`);
  },

  async getLatest(babyId: string) {
    return request<Record<string, any>>(`/babies/${babyId}/events/latest`);
  },

  async getStats(babyId: string) {
    return request<any>(`/babies/${babyId}/stats`);
  },

  async getById(eventId: string) {
    return request<any>(`/events/${eventId}`);
  },

  async update(
    eventId: string,
    data: {
      timestamp?: string;
      encryptedData?: string;
      encryptedNotes?: string;
      photoIds?: string[];
    }
  ) {
    return request<any>(`/events/${eventId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async delete(eventId: string) {
    return request(`/events/${eventId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// PHOTO API
// ============================================

export const photoApi = {
  async getUploadUrl(
    babyId: string,
    data: { mimeType: string; size: number; encryptedKey: string }
  ) {
    return request<{ uploadUrl: string; photoId: string; r2Key: string }>(
      `/babies/${babyId}/photos/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Direct upload through the backend (avoids CORS issues with R2)
   */
  async uploadDirect(
    babyId: string,
    data: { mimeType: string; encryptedKey: string },
    encryptedFile: ArrayBuffer
  ): Promise<ApiResponse<{ photoId: string; r2Key: string }>> {
    const url = `${API_URL}/babies/${babyId}/photos/upload`;

    const formData = new FormData();
    formData.append('file', new Blob([encryptedFile]), 'photo.enc');
    formData.append('mimeType', data.mimeType);
    formData.append('encryptedKey', data.encryptedKey);

    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    // If unauthorized, try to refresh token
    if (response.status === 401 && refreshToken) {
      try {
        await refreshAccessToken();
        headers['Authorization'] = `Bearer ${accessToken}`;
        response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData,
        });
      } catch {
        clearTokens();
        throw new Error('Session expired. Please log in again.');
      }
    }

    const result = (await response.json()) as ApiResponse<{ photoId: string; r2Key: string }>;

    if (!response.ok || !result.success) {
      throw new Error(result.error || `Upload failed: ${response.status}`);
    }

    return result;
  },

  async list(babyId: string, params?: { page?: number; limit?: number }) {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return request<any[]>(`/babies/${babyId}/photos${query}`);
  },

  async getDownloadUrl(photoId: string) {
    return request<{ downloadUrl: string; expiresAt: string }>(
      `/photos/${photoId}/download-url`
    );
  },

  /**
   * Direct download through the backend (avoids CORS issues with R2)
   */
  async downloadDirect(photoId: string): Promise<{ blob: Blob; mimeType: string }> {
    const url = `${API_URL}/photos/${photoId}/download`;

    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(url, { headers });

    // If unauthorized, try to refresh token
    if (response.status === 401 && refreshToken) {
      try {
        await refreshAccessToken();
        headers['Authorization'] = `Bearer ${accessToken}`;
        response = await fetch(url, { headers });
      } catch {
        clearTokens();
        throw new Error('Session expired. Please log in again.');
      }
    }

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const mimeType = response.headers.get('X-Mime-Type') || 'application/octet-stream';

    return { blob, mimeType };
  },

  async getById(photoId: string) {
    return request<any>(`/photos/${photoId}`);
  },

  async delete(photoId: string) {
    return request(`/photos/${photoId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// INVITE API
// ============================================

export const inviteApi = {
  async getByToken(token: string) {
    return request<any>(`/invites/${token}`);
  },

  async accept(token: string, data: { encryptedBabyKey: string }) {
    return request<any>(`/invites/${token}/accept`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async create(
    babyId: string,
    data: { email?: string; role?: string; expiresInDays?: number; encryptedBabyKey: string; token: string }
  ) {
    return request<any>(`/babies/${babyId}/invites`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listForBaby(babyId: string) {
    return request<any[]>(`/babies/${babyId}/invites`);
  },

  async delete(inviteId: string) {
    return request(`/invites/${inviteId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// SHARE API
// ============================================

export const shareApi = {
  async access(token: string) {
    return request<any>(`/shares/${token}`);
  },

  async create(
    babyId: string,
    data: {
      encryptedKey: string;
      eventTypes?: string[];
      dateFrom?: string;
      dateTo?: string;
      expiresInDays?: number;
      maxAccessCount?: number;
      recipientEmail?: string;
      recipientName?: string;
    }
  ) {
    return request<any>(`/babies/${babyId}/shares`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listForBaby(babyId: string) {
    return request<any[]>(`/babies/${babyId}/shares`);
  },

  async revoke(shareId: string) {
    return request(`/shares/${shareId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// SUPPORT API
// ============================================

export const supportApi = {
  async createTicket(data: { subject: string; description: string; priority?: string; ticketType?: string }) {
    return request<any>('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listTickets(params?: { status?: string; page?: number; limit?: number }) {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return request<any[]>(`/support/tickets${query}`);
  },

  async getTicket(ticketId: string) {
    return request<any>(`/support/tickets/${ticketId}`);
  },

  async uploadScreenshot(ticketId: string, file: File): Promise<void> {
    const url = `${API_URL}/support/tickets/${ticketId}/screenshots`;

    const formData = new FormData();
    formData.append('file', file, file.name);

    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401 && refreshToken) {
      try {
        await refreshAccessToken();
        headers['Authorization'] = `Bearer ${accessToken}`;
        response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData,
        });
      } catch {
        clearTokens();
        throw new Error('Session expired. Please log in again.');
      }
    }

    const result = (await response.json()) as ApiResponse<unknown>;

    if (!response.ok || !result.success) {
      throw new Error(result.error || `Upload failed: ${response.status}`);
    }
  },
};

// ============================================
// ADMIN API
// ============================================

export const adminApi = {
  async getAnalytics() {
    return request<any>('/admin/analytics');
  },

  async listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    emailVerified?: boolean;
    twoFactorEnabled?: boolean;
    isLocked?: boolean;
  }) {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return request<any[]>(`/admin/users${query}`);
  },

  async getUserDetails(userId: string) {
    return request<any>(`/admin/users/${userId}`);
  },

  async sendPasswordReset(userId: string) {
    return request<{ message: string }>(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
    });
  },

  async disable2FA(userId: string) {
    return request<{ message: string }>(`/admin/users/${userId}/disable-2fa`, {
      method: 'POST',
    });
  },

  async lockAccount(userId: string) {
    return request<{ message: string }>(`/admin/users/${userId}/lock`, {
      method: 'POST',
    });
  },

  async unlockAccount(userId: string) {
    return request<{ message: string }>(`/admin/users/${userId}/unlock`, {
      method: 'POST',
    });
  },
};

// ============================================
// EMERGENCY API
// ============================================

export const emergencyApi = {
  // Public lookup by email
  async lookupByEmail(email: string) {
    return request<Array<{ contactId: string; contactName: string; relationship: string; addedOn: string }>>(
      `/emergency-lookup?email=${encodeURIComponent(email)}`
    );
  },

  async addContact(
    babyId: string,
    data: {
      email: string;
      name: string;
      relationship: string;
      encryptedAccessPacket: string;
    }
  ) {
    return request<any>(`/babies/${babyId}/emergency-contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listContacts(babyId: string) {
    return request<any[]>(`/babies/${babyId}/emergency-contacts`);
  },

  async updateContact(
    contactId: string,
    data: {
      name?: string;
      relationship?: string;
      encryptedAccessPacket?: string;
      isActive?: boolean;
    }
  ) {
    return request<any>(`/emergency-contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async removeContact(contactId: string) {
    return request(`/emergency-contacts/${contactId}`, {
      method: 'DELETE',
    });
  },

  // Public endpoints for emergency contacts
  async requestAccess(contactId: string, reason?: string) {
    return request<any>(`/emergency-contacts/${contactId}/request-access`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  async getRequestStatus(requestId: string, token: string) {
    return request<any>(`/emergency-requests/${requestId}?token=${token}`);
  },

  async accessEmergencyPacket(requestId: string, token: string) {
    return request<{ encryptedAccessPacket: string; expiresAt: string }>(
      `/emergency-requests/${requestId}/access?token=${token}`
    );
  },

  // Primary user endpoints
  async approveRequest(requestId: string) {
    return request(`/emergency-requests/${requestId}/approve`, {
      method: 'POST',
    });
  },

  async denyRequest(requestId: string) {
    return request(`/emergency-requests/${requestId}/deny`, {
      method: 'POST',
    });
  },

  async revokeAccess(requestId: string) {
    return request(`/emergency-requests/${requestId}/revoke`, {
      method: 'POST',
    });
  },
};

export const analyticsApi = {
    getGrowthEvents(babyId: string) {
        return request<any[]>(`/babies/${babyId}/analytics/growth`);
    },
    getPercentileCurves(gender: string, metric: string) {
        const params = new URLSearchParams({
            gender,
            metric,
            maxAge: '36',
        });
        return request<any[]>(`/analytics/percentile-curves?${params.toString()}`);
    },
};

export const reminderApi = {
  async create(babyId: string, data: { type: string; message: string; triggerAt: string; recurrenceType?: string; recurrenceInterval?: number }) {
    return request<any>(`/babies/${babyId}/reminders`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listForBaby(babyId: string) {
    return request<any[]>(`/babies/${babyId}/reminders`);
  },

  async update(reminderId: string, data: { message?: string; triggerAt?: string; status?: string }) {
    return request<any>(`/reminders/${reminderId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async delete(reminderId: string) {
    return request(`/reminders/${reminderId}`, {
      method: 'DELETE',
    });
  },
};

export const notificationApi = {
  async getPublicKey() {
    return request<{ key: string }>('/notifications/vapid-public-key');
  },

  async subscribe(subscription: PushSubscription) {
    return request('/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  },

  async test() {
      return request('/notifications/test', {
          method: 'POST'
      });
  }
};

export const timerApi = {
  async startTimer(babyId: string, data: {
    type: 'FEED' | 'SLEEP' | 'UPRIGHT' | 'TUMMY' | 'KICK_COUNT' | 'CONTRACTION';
    side?: 'LEFT' | 'RIGHT';
    sleepType?: 'NAP' | 'NIGHT';
    sideSwitches?: Array<{ side: 'LEFT' | 'RIGHT'; switchedAt: number }>;
    targetDurationMs?: number;
  }) {
    return request(`/babies/${babyId}/timers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listTimers(babyId: string) {
    return request(`/babies/${babyId}/timers`);
  },

  async pauseTimer(timerId: string) {
    return request(`/timers/${timerId}/pause`, {
      method: 'PATCH',
    });
  },

  async resumeTimer(timerId: string) {
    return request(`/timers/${timerId}/resume`, {
      method: 'PATCH',
    });
  },

  async switchSide(timerId: string, side: 'LEFT' | 'RIGHT') {
    return request(`/timers/${timerId}/side`, {
      method: 'PATCH',
      body: JSON.stringify({ side }),
    });
  },

  async stopTimer(timerId: string) {
    return request(`/timers/${timerId}`, {
      method: 'DELETE',
    });
  },
};

export default {
  auth: authApi,
  baby: babyApi,
  event: eventApi,
  photo: photoApi,
  invite: inviteApi,
  share: shareApi,
  support: supportApi,
  emergency: emergencyApi,
  analytics: analyticsApi,
  reminder: reminderApi,
  notification: notificationApi,
  timer: timerApi,
};
